import { useCallback, useEffect, useMemo, useState } from 'react'
import { Download, FileText, X } from 'lucide-react'
import { Spinner } from '@neo-agents/ui'

interface FilePreviewProps {
  src: string
  mimeType: string
  fileName: string
  open: boolean
  onClose: () => void
}

function getExtension(name: string) {
  const parts = name.toLowerCase().split('.')
  return parts.length > 1 ? parts[parts.length - 1] ?? '' : ''
}

function isPdfFile(mimeType: string, fileName: string) {
  return mimeType === 'application/pdf' || getExtension(fileName) === 'pdf'
}

function isTextPreviewFile(mimeType: string, fileName: string) {
  const extension = getExtension(fileName)
  return mimeType.startsWith('text/') || extension === 'txt' || extension === 'csv'
}

export function isPreviewableFile(mimeType: string, fileName: string) {
  return isPdfFile(mimeType, fileName) || isTextPreviewFile(mimeType, fileName)
}

export function FilePreview({ src, mimeType, fileName, open, onClose }: FilePreviewProps) {
  const [loading, setLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [textContent, setTextContent] = useState<string>('')
  const [loadError, setLoadError] = useState<string | null>(null)

  const previewKind = useMemo(() => {
    if (isPdfFile(mimeType, fileName)) return 'pdf'
    if (isTextPreviewFile(mimeType, fileName)) return 'text'
    return 'unsupported'
  }, [fileName, mimeType])

  useEffect(() => {
    if (!open) {
      setLoading(false)
      setTextContent('')
      setLoadError(null)
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev)
        return null
      })
      return
    }

    let cancelled = false
    let objectUrl: string | null = null

    async function loadPreview() {
      if (previewKind === 'unsupported') {
        setLoadError('This file type is not supported for preview yet.')
        return
      }

      setLoading(true)
      setLoadError(null)

      try {
        const response = await fetch(src, {
          credentials: 'include',
          referrerPolicy: 'no-referrer',
          headers: {
            'Content-Disposition': 'inline',
          },
        })

        if (!response.ok) {
          throw new Error(`Failed to load preview (${response.status})`)
        }

        if (previewKind === 'pdf') {
          const blob = await response.blob()
          const normalizedBlob =
            blob.type === 'application/pdf'
              ? blob
              : new Blob([blob], { type: 'application/pdf' })
          objectUrl = URL.createObjectURL(normalizedBlob)
          if (cancelled) {
            URL.revokeObjectURL(objectUrl)
            return
          }
          setPreviewUrl(objectUrl)
          return
        }

        const text = await response.text()
        if (!cancelled) {
          setTextContent(text)
        }
      } catch (error) {
        if (!cancelled) {
          setLoadError(error instanceof Error ? error.message : 'Failed to load preview')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void loadPreview()

    return () => {
      cancelled = true
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [open, previewKind, src])

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  const handleSave = useCallback(async () => {
    if (isSaving) return
    setIsSaving(true)
    try {
      const response = await fetch(src, { credentials: 'include', referrerPolicy: 'no-referrer' })
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      window.open(src, '_blank', 'noopener,noreferrer')
    } finally {
      setIsSaving(false)
    }
  }, [fileName, isSaving, src])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70" />

      <div className="absolute right-4 top-4 z-10 flex items-center gap-2">
        <button
          className={`rounded-base border-2 border-white/30 bg-black/50 p-2 transition-colors ${isSaving ? 'cursor-not-allowed text-white/50' : 'text-white hover:bg-black/70'}`}
          onClick={(e) => {
            e.stopPropagation()
            void handleSave()
          }}
          disabled={isSaving}
          title="Download file"
        >
          {isSaving ? <Spinner className="h-5 w-5" /> : <Download className="h-5 w-5" />}
        </button>
        <button
          className="rounded-base border-2 border-white/30 bg-black/50 p-2 text-white transition-colors hover:bg-black/70"
          onClick={(e) => {
            e.stopPropagation()
            onClose()
          }}
          title="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div
        className="relative z-10 flex h-[90vh] w-[90vw] max-w-6xl flex-col overflow-hidden rounded-base border-2 border-white/20 bg-secondary-bg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-white/10 px-5 py-4">
          <div className="rounded-base border-2 border-white/15 bg-black/20 p-2 text-white/80">
            <FileText className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="truncate text-base font-heading text-white">{fileName}</p>
            <p className="text-xs text-white/55">{mimeType || 'file'}</p>
          </div>
        </div>

        <div className="relative min-h-0 flex-1 bg-black/20">
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <Spinner className="text-white text-2xl" />
            </div>
          ) : loadError ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
              <FileText className="h-10 w-10 text-white/45" />
              <div>
                <p className="text-sm font-heading text-white">Preview unavailable</p>
                <p className="mt-1 text-sm text-white/60">{loadError}</p>
              </div>
            </div>
          ) : previewKind === 'pdf' && previewUrl ? (
            <iframe
              src={previewUrl}
              title={fileName}
              className="h-full w-full bg-white"
            />
          ) : (
            <div className="h-full overflow-auto p-5">
              <pre className="min-h-full whitespace-pre-wrap rounded-base border-2 border-white/10 bg-black/20 p-4 font-mono text-sm leading-6 text-white/90">
                {textContent}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
